import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { QueryBuilder } from 'react-querybuilder';
import type { ColumnDef } from '@tanstack/react-table';
import * as XLSX from 'xlsx';
import 'react-querybuilder/dist/query-builder.css';

/** Excel sheet names: max 31 chars, no \ / * ? : [ ] */
function toSheetName(label: string): string {
  return label.replace(/[\\/*?:\u005B\u005D]/g, '_').slice(0, 31) || 'Sheet';
}
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { DataGrid } from '../components/data-grid';
import {
  getExplorerTables,
  getExplorerTableSchema,
  postExplorerQuery,
  postExplorerExport,
} from '../api/explorerDataApi';
import type {
  TableListItem,
  SchemaColumn,
  FilterGroup,
  SortItem,
} from '../types/explorer';
import { useGlobalProject } from '../contexts/GlobalProjectContext';
import { ArrowDownTrayIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';

/** Fetch all rows in one request (up to backend limit, same as export). */
const EXPLORER_PAGE_SIZE = 1_000_000;

const defaultQuery: FilterGroup = {
  combinator: 'and',
  rules: [],
};

export default function Explorer() {
  const { selectedProject, snapshots, selectedSnapshotId, snapshotsLoading } = useGlobalProject();
  const [searchParams] = useSearchParams();
  const tableParam = searchParams.get('table');
  const [selectedTable, setSelectedTable] = useState<TableListItem | null>(null);
  const [filterQuery, setFilterQuery] = useState<FilterGroup>(defaultQuery);
  const [sort, setSort] = useState<SortItem[]>([]);
  const [exportAllLoading, setExportAllLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  /** When enabled, keep selected import scope but remove proj_id filtering. */
  const [explorerUnscoped, setExplorerUnscoped] = useState(false);
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchDebounced('');
      return;
    }
    const t = setTimeout(() => setSearchDebounced(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  /** Use global active project; scope rows by selected snapshot’s import when available (matches overview). */
  const projectIdForApi = selectedProject?.id ?? null;
  const importRunIdForApi = useMemo(() => {
    if (!selectedSnapshotId) return undefined;
    const s = snapshots.find((x) => x.id === selectedSnapshotId);
    return s?.import_run_id;
  }, [snapshots, selectedSnapshotId]);

  const { data: tablesData } = useQuery({
    queryKey: ['explorer', 'tables'],
    queryFn: () => getExplorerTables(),
  });
  const tables = useMemo(() => tablesData?.tables ?? [], [tablesData?.tables]);

  /** Preselect table from URL ?table= when tables are loaded */
  useEffect(() => {
    if (tableParam && tables.length > 0) {
      const match = tables.find((t) => t.id === tableParam);
      if (match && selectedTable?.id !== tableParam) setSelectedTable(match);
    }
  }, [tableParam, tables, selectedTable?.id]);

  const { data: schemaData } = useQuery({
    queryKey: ['explorer', 'schema', selectedTable?.id],
    queryFn: () => getExplorerTableSchema(selectedTable!.id),
    enabled: !!selectedTable?.id,
  });
  const schema = useMemo(() => schemaData?.columns ?? [], [schemaData?.columns]);
  const schemaMap = useMemo(
    () => Object.fromEntries(schema.map((c: SchemaColumn) => [c.name, c.type])),
    [schema]
  );

  const queryPayload = useMemo(
    () =>
      selectedTable
        ? {
            table: selectedTable.id,
            filters: filterQuery,
            sort: sort.length ? sort : undefined,
            page: 0,
            pageSize: EXPLORER_PAGE_SIZE,
            ...(explorerUnscoped && tablesData?.allow_unscoped
              ? { unscoped: true as const }
              : {
                  project_id: projectIdForApi ?? undefined,
                  import_run_id: importRunIdForApi,
                }),
            includeTotalRowCount: true,
            search: searchDebounced || undefined,
          }
        : null,
    [
      selectedTable,
      filterQuery,
      sort,
      projectIdForApi,
      importRunIdForApi,
      searchDebounced,
      explorerUnscoped,
      tablesData?.allow_unscoped,
    ]
  );

  /** Avoid querying with missing snapshot scope: fetchSnapshots resets selection then sets latest import_run_id. */
  const explorerDataScopeReady =
    !selectedProject?.id ||
    explorerUnscoped ||
    !snapshotsLoading;

  const { data: queryData, isLoading: queryLoading, error: queryError } = useQuery({
    queryKey: ['explorer', 'query', queryPayload],
    queryFn: () => postExplorerQuery(queryPayload!),
    enabled: !!queryPayload && explorerDataScopeReady,
  });
  const rows = queryData?.rows ?? [];
  const totalRowCount = queryData?.totalRowCount ?? null;

  /** Export the currently selected table as a single CSV. */
  const handleExportThisTable = useCallback(async () => {
    if (!selectedTable) return;
    try {
      const blob = await postExplorerExport({
        table: selectedTable.id,
        filters: filterQuery,
        sort: sort.length ? sort : undefined,
        ...(explorerUnscoped && tablesData?.allow_unscoped
          ? { unscoped: true }
          : {
              project_id: projectIdForApi ?? undefined,
              import_run_id: importRunIdForApi,
            }),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `explorer-${selectedTable.id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    }
  }, [
    selectedTable,
    filterQuery,
    sort,
    projectIdForApi,
    importRunIdForApi,
    explorerUnscoped,
    tablesData?.allow_unscoped,
  ]);

  /** Export all tables as one Excel workbook (one sheet per table; no filters). */
  const handleExportAllTables = useCallback(async () => {
    if (!tables.length) return;
    setExportAllLoading(true);
    try {
      const wb = XLSX.utils.book_new();
      for (const table of tables) {
        const blob = await postExplorerExport({
          table: table.id,
          filters: defaultQuery,
          sort: undefined,
          ...(explorerUnscoped && tablesData?.allow_unscoped
            ? { unscoped: true }
            : {
                project_id: projectIdForApi ?? undefined,
                import_run_id: importRunIdForApi,
              }),
        });
        const csvText = await blob.text();
        const csvBook = XLSX.read(csvText, { type: 'string', raw: true });
        const firstSheetName = csvBook.SheetNames[0];
        const sheet = firstSheetName ? csvBook.Sheets[firstSheetName] : null;
        if (sheet) {
          const sheetName = toSheetName(table.label ?? table.id);
          XLSX.utils.book_append_sheet(wb, sheet, sheetName);
        }
      }
      XLSX.writeFile(wb, 'explorer-all-tables.xlsx');
    } catch (e) {
      console.error('Export all failed:', e);
    } finally {
      setExportAllLoading(false);
    }
  }, [
    tables,
    projectIdForApi,
    importRunIdForApi,
    explorerUnscoped,
    tablesData?.allow_unscoped,
  ]);

  const fields = useMemo(
    () =>
      schema.map((c: SchemaColumn) => ({
        name: c.name,
        label: c.name,
        inputType: c.type?.toLowerCase().includes('int') || c.type?.toLowerCase().includes('double')
          ? 'number'
          : c.type?.toLowerCase().includes('date')
            ? 'date'
            : 'text',
      })),
    [schema]
  );

  const gridColumns = useMemo<ColumnDef<Record<string, unknown>, unknown>[]>(
    () =>
      schema.length
        ? schema.map((c: SchemaColumn) => ({
            id: c.name,
            accessorKey: c.name,
            header: c.name,
            minSize: 80,
            maxSize: 2400,
          }))
        : [],
    [schema]
  );

  return (
    <MainLayout title="Raw Data Viewer">
      <div className="flex flex-col h-full min-h-0 min-w-0 p-4 overflow-hidden">
        <div className="flex flex-1 min-h-0 min-w-0 gap-4 overflow-hidden">
          <aside className="w-56 shrink-0 border border-border rounded-lg p-3 bg-card overflow-auto">
            <h3 className="text-sm font-medium mb-2">Tables</h3>
            <ul className="space-y-1">
              {tables.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedTable(t);
                      setFilterQuery(defaultQuery);
                      setSort([]);
                    }}
                    className={`w-full text-left px-2 py-1.5 rounded text-sm ${
                      selectedTable?.id === t.id
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <span className="font-medium">{t.label}</span>
                    {t.subtitle && (
                      <span className="block text-xs opacity-80">{t.subtitle}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div className="flex-1 flex flex-col min-w-0 min-h-0 border border-border rounded-lg bg-card p-4 overflow-hidden">
            {!selectedTable ? (
              <div className="flex items-center justify-center flex-1 text-muted-foreground">
                Select a table from the left.
              </div>
            ) : (
              <>
                {tablesData?.allow_unscoped && (
                  <div className="shrink-0 mb-2">
                    <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer max-w-xl">
                      <input
                        type="checkbox"
                        checked={explorerUnscoped}
                        onChange={(e) => setExplorerUnscoped(e.target.checked)}
                        className="rounded border-input"
                      />
                      <span>
                        Show all data in this import (no proj_id filter). Requires{' '}
                        <code className="text-xs bg-muted px-1 rounded">EXPLORER_ALLOW_UNSCOPED=1</code> on the
                        API.
                      </span>
                    </label>
                  </div>
                )}
                <div className="shrink-0 mb-3">
                  <div className="text-xs text-muted-foreground mb-1">Filters</div>
                  <div className="explorer-query-builder">
                    <QueryBuilder
                      query={filterQuery}
                      onQueryChange={(q) => setFilterQuery(q as FilterGroup)}
                      fields={fields}
                      addRuleToNewGroups
                      showCombinatorsBetweenRules
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2 shrink-0 flex-wrap">
                  <input
                    type="search"
                    placeholder="Search in table (all data)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-9 px-3 rounded-md border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 w-56"
                    aria-label="Search in table"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportThisTable}
                    disabled={!rows.length}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Export this table (CSV)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportAllTables}
                    disabled={!tables.length || exportAllLoading}
                  >
                    <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                    {exportAllLoading ? 'Exporting…' : 'Export all tables (XLSX)'}
                  </Button>
                  <span className="text-sm text-muted-foreground ml-1">
                    {queryLoading
                      ? '—'
                      : totalRowCount != null
                        ? `${Number(totalRowCount).toLocaleString()} Rows${searchDebounced ? ' (matching search)' : ''}`
                        : `${rows.length} rows loaded`}
                  </span>
                </div>
                {queryError && (
                  <div className="text-sm text-destructive mb-2 shrink-0">
                    {(queryError as Error).message}
                  </div>
                )}
                <div
                  className="flex-1 min-h-0 min-w-0 flex flex-col border rounded overflow-hidden"
                  style={{ minHeight: '50vh' }}
                >
                  <DataGrid<Record<string, unknown>>
                    data={rows}
                    columns={gridColumns}
                    getRowId={(_row, index) => String(index)}
                    density="compact"
                    enableQuickFilter={false}
                    enableColumnResize
                    enableColumnPinning
                    enableSorting={false}
enableCopy={false}
                        enableCsvExport={false}
                    scrollContainerClassName="flex-1 min-h-0"
                    scrollContainerStyle={{ minHeight: 300 }}
                    loading={queryLoading}
                    error={queryError ? String((queryError as Error).message) : null}
                    emptyMessage="No rows. Adjust filters or select another table."
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
